import { compareHuge, huge, type HugeNumber } from "@/shared/numbers";
import {
  normalizeNHovGrowthExponentBonus,
  resolveNHovBranchEffectiveLevel,
} from "./nHovBranchFormulaMath";
import type { NHovTransferBalance } from "@/features/hoverIdle/balance/hovResetBalanceTypes";

export const HG_NHOV_EFFECTS_TO_HNG_TRANSFER_MULTIPLIER_AT_LEVEL_ZERO = 0;
export const HG_NHOV_EFFECTS_TO_HNG_REWARD_AT_REFERENCE = 1.2;
export const HG_NHOV_EFFECTS_TO_HNG_EFFECTIVE_LEVEL_EXPONENT = 0.9;

function isFiniteNonNegative(value: number): boolean {
  return Number.isFinite(value) && value >= 0;
}

function sanitizeTransferMultiplierHuge(value: HugeNumber): HugeNumber {
  if (
    !value.isFinite() ||
    compareHuge(
      value,
      HG_NHOV_EFFECTS_TO_HNG_TRANSFER_MULTIPLIER_AT_LEVEL_ZERO,
    ) <= 0
  ) {
    return huge(HG_NHOV_EFFECTS_TO_HNG_TRANSFER_MULTIPLIER_AT_LEVEL_ZERO);
  }
  return value;
}

// HugeNumber-preserving core so the transfer multiplier grows past the JS
// number limit once the `outside-nhov` cap is lifted.
export function calculateHgNHovEffectsToHngTransferMultiplierHuge(
  level: number,
  exponentBonus = 0,
  balance?: NHovTransferBalance,
): HugeNumber {
  if (!isFiniteNonNegative(level) || level <= 0) {
    return huge(HG_NHOV_EFFECTS_TO_HNG_TRANSFER_MULTIPLIER_AT_LEVEL_ZERO);
  }

  const effectiveLevel = resolveNHovBranchEffectiveLevel({
    level,
    exponent:
      (balance?.effectiveLevelExponent ??
        HG_NHOV_EFFECTS_TO_HNG_EFFECTIVE_LEVEL_EXPONENT) +
      normalizeNHovGrowthExponentBonus(exponentBonus),
  });

  return sanitizeTransferMultiplierHuge(
    huge(
      balance?.rewardAtReference ?? HG_NHOV_EFFECTS_TO_HNG_REWARD_AT_REFERENCE,
    )
      .pow(effectiveLevel)
      .sub(1),
  );
}

export interface HgNHovEffectsToHngPreview {
  readonly current: HugeNumber;
  readonly gain: HugeNumber;
  readonly next: HugeNumber;
}

export function previewHgNHovEffectsToHng(options: {
  readonly currentLevel: number;
}): HgNHovEffectsToHngPreview {
  const current = calculateHgNHovEffectsToHngTransferMultiplierHuge(
    options.currentLevel,
  );
  const next = calculateHgNHovEffectsToHngTransferMultiplierHuge(
    options.currentLevel + 1,
  );
  const gain = next.sub(current);
  return { current, gain, next };
}

export function calculateHgAmplifyToHngGainMultiplierHuge(options: {
  readonly level: number;
  readonly hgAmplifyMultiplier: HugeNumber;
  readonly exponentBonus?: number;
  readonly transferMultiplierOverride?: HugeNumber;
  readonly balance?: NHovTransferBalance;
}): HugeNumber {
  const transferMultiplier =
    options.transferMultiplierOverride ??
    calculateHgNHovEffectsToHngTransferMultiplierHuge(
      options.level,
      options.exponentBonus,
      options.balance,
    );
  if (compareHuge(transferMultiplier, 0) <= 0) return huge(1);
  if (
    !options.hgAmplifyMultiplier.isFinite() ||
    compareHuge(options.hgAmplifyMultiplier, 1) <= 0
  ) {
    return huge(1);
  }

  return huge(1).add(
    options.hgAmplifyMultiplier.sub(1).mul(transferMultiplier),
  );
}

export function calculateHgDiscountToHngCostDivisorHuge(options: {
  readonly hgDiscountCostDivisor: HugeNumber;
  readonly hgNHovEffectsToHngTransferMultiplier: HugeNumber;
}): HugeNumber {
  if (
    !options.hgDiscountCostDivisor.isFinite() ||
    compareHuge(options.hgDiscountCostDivisor, 1) <= 0
  ) {
    return huge(1);
  }
  if (
    !options.hgNHovEffectsToHngTransferMultiplier.isFinite() ||
    compareHuge(options.hgNHovEffectsToHngTransferMultiplier, 0) <= 0
  ) {
    return huge(1);
  }

  const divisor = options.hgDiscountCostDivisor.mul(
    options.hgNHovEffectsToHngTransferMultiplier,
  );
  return compareHuge(divisor, 1) <= 0 ? huge(1) : divisor;
}
